import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { SimpleScheduleForm } from '@/components/schedule-wizard/simple-schedule-form';
import { schoolsService } from '@/services/schools.service';
import { useSchoolSchedules } from '@/hooks/use-schools';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import type { Schedule } from '@/types/api';

export function ScheduleDetailPage() {
  const { schoolId, scheduleId } = useParams<{ schoolId: string; scheduleId: string }>();
  const navigate = useNavigate();
  const { data: schedules, isLoading, error } = useSchoolSchedules(schoolId!);

  const schedule = schedules?.find((s) => s.id === scheduleId);

  const handleSave = async (updatedSchedule: Partial<Schedule>) => {
    try {
      await schoolsService.updateSchedule(schoolId!, scheduleId!, updatedSchedule);
      toast.success('Schedule updated successfully');
      navigate(`/dashboard/schools/${schoolId}#schedules`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update schedule');
      throw error;
    }
  };

  const handleDelete = async () => {
    try {
      await schoolsService.deleteSchedule(schoolId!, scheduleId!);
      toast.success('Schedule deleted successfully');
      navigate(`/dashboard/schools/${schoolId}#schedules`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete schedule');
      throw error;
    }
  };

  const handleCancel = () => {
    navigate(`/dashboard/schools/${schoolId}#schedules`);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-4xl space-y-6 p-6">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !schedule) {
    return (
      <div className="container mx-auto max-w-4xl space-y-6 p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error ? 'Failed to load schedule' : 'Schedule not found'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-4xl space-y-8 p-6">
      <div className="flex items-center gap-3">
        <Button 
          variant="outline" 
          size="icon"
          asChild
          className="h-10 w-10 rounded-lg transition-all hover:shadow-md hover:-translate-y-0.5"
        >
          <Link to={`/dashboard/schools/${schoolId}#schedules`}>
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight">Edit Schedule</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{schedule.displayName}</p>
        </div>
      </div>

      <SimpleScheduleForm
        schoolId={schoolId!}
        existingSchedule={schedule}
        onSave={handleSave}
        onCancel={handleCancel}
        onDelete={handleDelete}
      />
    </div>
  );
}